import * as yup from "yup";
import {
    isValidPhoneNumber,
    parsePhoneNumberFromString,
    getCountryCallingCode,
} from "libphonenumber-js";
import { getPhoneCountry } from "../pages/PhoneCountrySession";

/*
Shared phone helpers for every <PhoneInput> in the app.

Replaces the per-page `phoneNumberRules` tables, which hardcoded min/max digit
counts for sg/nz/gb/au only and keyed them off the dial code. Dial codes are not
unique to a country (+1 US/CA/Caribbean, +44 GB/JE/GG/IM, +7 RU/KZ), so once the
dropdown lists every country the rules have to key off iso2 instead.

Note: the sibling form field is still named `*_countryCode` for continuity with
the existing forms, but it now holds an ISO2 code ("gb"), not a dial code ("44").
The backend never read that field — it is validation-only state.
*/

// Centres are mostly in these four, so float them to the top of the country list.
export const PREFERRED_COUNTRIES = ["sg", "nz", "gb", "au"];

export const SEARCH_PLACEHOLDER = "Search country or code";

/*
react-phone-input-2 holds its value without a leading "+" ("6591234567").
libphonenumber-js needs E.164, so add it back before parsing.
*/
function digitsOf(value) {
    return String(value || "").replace(/[^\d]/g, "");
}

function toE164(value) {
    const digits = digitsOf(value);
    return digits ? `+${digits}` : "";
}

/*
The widget always keeps the dial code in the box, so a "cleared" field still reads
as e.g. "65". Treat a value that is only (part of) the dial code as empty, or
clearing an optional phone would raise a validation error.
*/
function isBareDialCode(value, iso) {
    const digits = digitsOf(value);
    if (!digits) return true;
    if (!iso) return false;

    let callingCode;
    try {
        callingCode = getCountryCallingCode(String(iso).toUpperCase());
    } catch (e) {
        return false;
    }
    return digits === callingCode || callingCode.startsWith(digits);
}

/*
Work out the iso2 for a stored number. Used to seed `*_countryCode` when a record
loads, so an untouched form still validates against the right country instead of
falling through to a generic length check.
*/
export function isoFromPhone(value, fallback) {
    const e164 = toE164(value);
    if (e164) {
        const parsed = parsePhoneNumberFromString(e164);
        if (parsed && parsed.country) return parsed.country.toLowerCase();
    }
    return fallback || getPhoneCountry();
}

/*
True when the field actually holds a number, rather than being empty or left with
just the dial code the widget always keeps in the box. Use this instead of a bare
`!value` check when deciding whether a phone was supplied.
*/
export function hasPhoneNumber(value, iso) {
    return !isBareDialCode(value, iso);
}

/*
Build the yup test for a phone field. `isoField` names the sibling form field
holding the iso2 (e.g. "st_countryCode").

Empty is allowed — every phone field in the app is optional, and required-ness is
layered on by the caller where it applies (e.g. par_phone on non-adult learners).
*/
export function phoneSchema(isoField) {
    return yup
        .string()
        .test("valid-phone-number", function (value) {
            const iso = this.parent[isoField] || isoFromPhone(value);
            if (isBareDialCode(value, iso)) return true;

            const country = iso ? String(iso).toUpperCase() : undefined;
            if (isValidPhoneNumber(toE164(value), country)) return true;

            return this.createError({
                message: "Please enter a valid phone number for the selected country.",
            });
        })
        .notRequired();
}

/*
Same as phoneSchema, but for fields that must be filled in.

yup's own .required() is not enough here: the widget keeps the dial code in the
box, so a cleared field still reads as "65" and satisfies .required(). Check with
hasPhoneNumber instead.
*/
export function requiredPhoneSchema(isoField, message) {
    return phoneSchema(isoField).test("phone-required", function (value) {
        if (hasPhoneNumber(value, this.parent[isoField])) return true;
        return this.createError({ message: message });
    });
}

/*
Standard props for every <PhoneInput>. Spread first so a page can still override.

  <PhoneInput {...phoneInputProps()} name="st_phone" value={values.st_phone} ... />

No `onlyCountries` — the full list is available and searchable. `enableSearch`
matches on dial code when the query is digits, otherwise on iso2 then name, so
"44", "+44", "gb" and "united king" all find the UK.
*/
export function phoneInputProps() {
    return {
        enableLongNumbers: true,
        countryCodeEditable: true,
        enableSearch: true,
        searchPlaceholder: SEARCH_PLACEHOLDER,
        searchNotFound: "No country found",
        preferredCountries: PREFERRED_COUNTRIES,
        country: getPhoneCountry(),
    };
}

/*
onChange handler shared by every phone field. Stores the number and the iso2 of
whichever country the widget resolved.

  onChange={phoneOnChange(values, setValues, "st_phone", "st_countryCode")}
*/
export function phoneOnChange(values, setValues, phoneField, isoField) {
    return (phone, country) =>
        setValues({
            ...values,
            [phoneField]: phone,
            [isoField]: country && country.countryCode ? country.countryCode : "",
        });
}
